<!-- @format -->

<template>
	<div class="drag-list">
		<div class="drag-list-content">
			<div class="panel-heading">
				<span>{{ title }}</span>
				<el-dropdown size="default" :hide-on-click="false" max-height="400" @command="onAdd">
					<el-button type="primary" link>添加</el-button>
					<template #dropdown>
						<el-dropdown-menu>
							<el-dropdown-item
								v-for="item in addList"
								:key="item.code"
								:command="item.code"
								:disabled="value?.some((i) => i.code === item.code)"
							>
								{{ item.label }}{{ item.code ? `(${item.code})` : '' }}
							</el-dropdown-item>
						</el-dropdown-menu>
					</template>
				</el-dropdown>
			</div>
			<draggable
				:list="value"
				item-key="code"
				class="params-drag"
				v-bind="{
					group: { name: 'drag-list', pull: false, put: false },
					sort: true,
					handle: '.params-drag-icon',
					ghostClass: 'params-drag-ghost',
					animation: 300,
				}"
			>
				<template #item="{ element }">
					<div class="drag-list-item">
						<span class="item-span" :title="`${element.label}${element.code && `(${element.code})`}  `">
							<span class="params-drag-icon">
								<el-icon size="16" style="display: inline"><Rank /></el-icon>
							</span>
							{{ element.label }}{{ element.code && `(${element.code})` }}
						</span>
						<span class="item-btn">
							<el-button
								v-if="canEdit"
								type="primary"
								link
								size="default"
								icon="Setting"
								@click.stop.prevent="onOpenDetail(element, 'edit')"
							/>
							<el-button type="primary" link size="default" icon="Delete" @click.stop.prevent="onDel(element)" />
						</span>
					</div>
				</template>
			</draggable>
		</div>
		<pc-dialog
			v-model="activeData.visabled"
			append-to-body
			destroy-on-close
			:title="title"
			:can-full-screen="true"
			:close-on-press-escape="false"
			width="80%"
			@on-confirm="onConfirm"
			@on-cancel="onCancel"
		>
			<el-form ref="detailRef" :model="activeData.formData" label-width="auto" label-position="right" size="default">
				<template v-for="item in detailFormList" :key="item.code">
					<template v-if="item.hidden ? !item.hidden(activeData.formData) : true">
						<component
							:is="componentsList[item.type] || getComponent(item.type)"
							v-if="item.isFormItem"
							:disabled="item.disabled ? item.disabled(activeData.formData) : false"
							v-bind="item.pcProps"
							:model-value="getValue(item.code)"
							@update:model-value="onChange($event, item.code)"
						/>
						<el-form-item v-else :prop="item.code" :label="item.label" size="default">
							<component
								:is="componentsList[item.type] || getComponent(item.type)"
								:disabled="item.disabled ? item.disabled(activeData.formData) : false"
								v-bind="item.pcProps"
								:model-value="getValue(item.code)"
								@update:model-value="onChange($event, item.code)"
							/>
						</el-form-item>
					</template>
				</template>
			</el-form>
		</pc-dialog>
	</div>
</template>

<script setup lang="ts">
	import { FormInstance } from 'element-plus';
	import { uuid, deepClone } from '@power/utils';
	import { draggable, getComponent, PcDialog, type ComponentType } from '@/components';

	import { chains } from '@/utils/util';

	interface Props {
		title: string;
		modelValue: any;
		detailFormList: ComponentType[];
		addList: any[];
		canEdit?: boolean;
		componentsList?: any;
	}

	const props = withDefaults(defineProps<Props>(), {
		canEdit: true,
		componentsList: () => ({}),
	});

	const emits = defineEmits(['update:modelValue', 'on-save']);

	const detailRef = ref<FormInstance>();

	const activeData = ref<{
		type: 'add' | 'edit';
		visabled: boolean;
		formData: any;
		idx: number;
	}>({
		type: 'add',
		visabled: false,
		formData: {},
		idx: 0,
	});

	const value = computed({
		get() {
			return props.modelValue || [];
		},
		set(value) {
			emits('update:modelValue', value);
		},
	});

	const getValue = (code) => {
		return chains(activeData.value.formData, code);
	};

	const onChange = (val, code) => {
		if (Array.isArray(code)) {
			const obj = chains(activeData.value.formData, code.slice(0, -1));
			const key = code.slice(-1)[0];
			obj[key] = val;
		} else {
			activeData.value.formData[code] = val;
		}
	};

	const onAdd = (code) => {
		const item: any = deepClone(props.addList.find((itm) => itm.code === code));
		if (!item.code) {
			item.code = uuid();
		}
		onOpenDetail(item, 'add');
		if (!item.showDetail) {
			onSave();
		}
	};

	const onOpenDetail = (item, type) => {
		const idx = value.value.findIndex((itm) => itm.code === item.code);
		activeData.value = {
			type,
			visabled: true,
			formData: JSON.parse(JSON.stringify(item)),
			idx,
		};
	};

	const onSave = () => {
		if (activeData.value.type === 'edit') {
			value.value[activeData.value.idx] = activeData.value.formData;
		} else {
			value.value.push(activeData.value.formData);
		}
		onCancel();
	};

	const onConfirm = (callback?: Function) => {
		callback && callback();
		detailRef.value?.validate(async (isVaild) => {
			if (isVaild) {
				onSave();
			}
		});
	};

	const onCancel = (callback?: Function) => {
		callback && callback();
		activeData.value.visabled = false;
	};

	const onDel = (item) => {
		const idx = value.value.findIndex((itm) => itm.code === item.code);
		value.value.splice(idx, 1);
	};
</script>

<style scoped lang="scss">
	.drag-list {
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: center;

		.drag-list-content {
			width: 100%;

			.panel-heading {
				color: #333;
				background-color: var(--theme-bg-color);
				margin: 5px 0px;
				padding: 10px;
				font-weight: 700;
				border-bottom: 1px solid #fff;
				display: flex;
				justify-content: space-between;
			}

			.drag-list-item {
				padding: 5px;
				display: flex;
				gap: 5px;
				justify-content: space-between;
				border: 1px solid var(--el-border-color);

				.item-span {
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					width: calc(100% - 50px);
				}

				.item-btn {
					.el-button + .el-button {
						margin-left: 0px;
					}
				}
			}

			.drag-list-item + .drag-list-item {
				margin-top: 5px;
			}
		}
	}

	.params-drag {
		max-height: 400px;
		overflow-y: auto;

		.params-drag-icon {
			padding: 2px 0px 0px 10px;
			cursor: move;
		}

		.params-drag-ghost {
			background-color: var(--theme-bg-color);
		}

		.params-checkbox {
			:deep(.el-checkbox__input) {
				margin-bottom: 14px;
			}
		}
	}
</style>
